import * as React from 'react';
import { css, Global, useTheme } from '@emotion/react';
import { CloseAlt, IconButton, Inline, Progress, Stack, Text, useForkRef, useToast } from '@resi-media/resi-ui';
import { createPortal } from 'react-dom';
import { generatePath, useNavigate, useParams } from 'react-router';
import { useIsMounted } from 'usehooks-ts';
import { LiveJoinNowButton } from '@sites/components/LiveJoinNowButton';
import { GOOGLE_EVENTS } from '@sites/constants/google-events';
import { LIVE_VIDEO_STATUS } from '@sites/constants/live-player';
import { useEnvironment } from '@sites/contexts/environmentContext';
import { useFeed } from '@sites/contexts/feedContext';
import { useLivePlayer } from '@sites/contexts/livePlayerContext';
import { usePlayerContext } from '@sites/contexts/playerContext';
import { useContinueWatching } from '@sites/hooks/useContinueWatching';
import { useThrottle } from '@sites/hooks/useThrottle';
import * as globalStyles from '@sites/styles/global';
import type { Playlists, Videos } from '@sites/types';
import { usePrefix } from '@studio/hooks/use-prefix';
import { S } from './styles';

const EMBED_TYPE = {
  LIBRARY: 'library',
  WEB_CHANNEL: 'webchannel',
} as const;

type _EmbedType = (typeof EMBED_TYPE)[keyof typeof EMBED_TYPE];

type PlayerScriptProps = {
  channelId?: string;
  isAutoPlay?: boolean;
  isMuted?: boolean;
  isScrubBar?: boolean;
  mode?: string;
  type?: _EmbedType;
  video?: Playlists.Derived.Video | Videos.Get.Video;
};

const PlayerScriptInternal = (
  {
    channelId,
    isAutoPlay = false,
    isMuted = false,
    isScrubBar = true,
    mode,
    type = EMBED_TYPE.LIBRARY,
    video: videoProp,
  }: PlayerScriptProps,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const { webplayerEnvAttribute, webplayerLoader, webplayerPageScript } = useEnvironment();
  const navigate = useNavigate();
  const videoPlayer = React.useRef<HTMLDivElement>(null);
  const handleRef = useForkRef(ref, videoPlayer);
  const { playerCommands, playerKey, playerState, playerStates, setPlayerDetails, setPlayerState, videoContainer } =
    usePlayerContext();
  const throttle = useThrottle();
  const isMounted = useIsMounted();
  const { customerSite, videoId } = useParams<'customerSite' | 'videoId'>();
  const { videos } = useFeed();
  const { isLive, setLiveVideoStatus, showJoinEarly, videoName, webChannelId } = useLivePlayer();
  const { remove, trigger } = useToast();
  const theme = useTheme();
  const { prefixNS } = usePrefix('components:', 'livePlayerToast');
  const hasTriggeredToast = React.useRef(false);
  const isLiveViewTracked = React.useRef(false);
  const fullScreenToastId = `${customerSite}-fullscreen-video-toast-container`;

  const currentVideo = React.useMemo(() => {
    if (type === EMBED_TYPE.WEB_CHANNEL) return null;
    return videoProp ?? videos.find((x) => x.id === videoId);
  }, [type, videoId, videoProp, videos]);

  React.useEffect(() => {
    const isInvalidLibraryVideo = type === EMBED_TYPE.LIBRARY && !videoProp && !videos.length && videoId;
    const isInvalidWebChannelVideo = type === EMBED_TYPE.WEB_CHANNEL && !channelId;
    if (isInvalidLibraryVideo || isInvalidWebChannelVideo) {
      navigate('/404');
    }
  }, [type, channelId, currentVideo, navigate, videoId, videoProp, videos.length]);

  const { getContinueWatchingVideo, removeContinueWatchingVideo, setContinueWatchingVideo } = useContinueWatching();

  React.useEffect(
    // Unable to test observers currently
    /* c8 ignore start */
    function observeVideoPlayer() {
      if (!videoPlayer.current) {
        return undefined;
      }
      const observer = new MutationObserver(() => {
        if (!isMounted()) {
          return;
        }
        const videoElement = document.getElementById('resi-video') as HTMLVideoElement | null;
        if (!videoElement) {
          return;
        }
        videoElement.onloadedmetadata = () => {
          if (!isMounted()) {
            return;
          }
          setPlayerDetails({
            duration: videoElement.duration,
            videoContainer: document.getElementById('resi-videoContainer') as HTMLElement,
          });
          if (channelId) {
            setLiveVideoStatus(LIVE_VIDEO_STATUS.STARTED);
          }
          videoElement.onpause = () => setPlayerState(playerCommands.PAUSE);
          videoElement.onplay = () => setPlayerState(playerCommands.PLAY);
          videoElement.ontimeupdate = () => {
            const { currentTime, paused } = videoElement;
            if (channelId && !isLiveViewTracked.current) {
              let totalWatched = 0;
              for (let i = 0; i < videoElement.played.length; i++) {
                totalWatched += videoElement.played.end(i) - videoElement.played.start(i);
              }

              if (totalWatched >= 60) {
                gtag('event', GOOGLE_EVENTS.LIVE_VIEWER);
                isLiveViewTracked.current = true;
              }
            }
            const recordCurrentTime = () => {
              if (videoId && currentVideo && currentTime) {
                setContinueWatchingVideo({
                  videoId,
                  playlistId: 'playlistId' in currentVideo ? currentVideo.playlistId : '',
                  currentTime: Math.round(currentTime),
                  duration: Math.round(videoElement.duration),
                });
              }
            };
            if (paused) {
              recordCurrentTime();
            } else {
              throttle(recordCurrentTime, 2000);
            }
          };
          videoElement.onended = () => {
            if (videoId) {
              removeContinueWatchingVideo(videoId);
            }
            if (channelId) {
              setLiveVideoStatus(LIVE_VIDEO_STATUS.ENDED);
              customerSite && remove(customerSite);
            }
          };
          videoElement.play();
        };
        observer.disconnect();
      });

      observer.observe(videoPlayer.current, { childList: true, subtree: true });
      return () => {
        observer.disconnect();
      };
    },
    /* c8 ignore stop */
    [
      channelId,
      currentVideo,
      customerSite,
      isMounted,
      navigate,
      playerCommands.PAUSE,
      playerCommands.PLAY,
      playerKey,
      prefixNS,
      remove,
      removeContinueWatchingVideo,
      setContinueWatchingVideo,
      setLiveVideoStatus,
      setPlayerDetails,
      setPlayerState,
      showJoinEarly,
      theme.palette.surface.canvas,
      theme.shape.borderRadius.m,
      throttle,
      trigger,
      videoId,
      videoName,
      webChannelId,
    ]
  );

  React.useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = Boolean(document.fullscreenElement);

      if (!channelId) {
        if (isLive && isFullscreen && !hasTriggeredToast.current) {
          hasTriggeredToast.current = true;

          customerSite &&
            trigger(
              {
                shouldCloseOnTimeout: false,
                id: fullScreenToastId,
                isPositionTop: true,
                children: (
                  <Stack alignItems="flex-start" id="live-video-toast-container">
                    <Inline alignItems="flex-start" justifyContent="space-between" width="scale">
                      <Stack gap="xs">
                        <Text variant="body3">{prefixNS('liveBroadcastHasStarted')}</Text>
                        <Text css={(theme) => globalStyles.truncateText(theme)} variant="h5" weightVariant="bold">
                          {videoName}
                        </Text>
                      </Stack>
                      <IconButton
                        colorVariant="neutral"
                        dataTestId="remove-toast-button"
                        label="Close"
                        onClick={() => remove(fullScreenToastId)}
                      >
                        <CloseAlt
                          css={(theme) => css`
                            color: ${theme.palette.contrast};
                            font-size: ${theme.typography.pxToRem(14)};
                          `}
                        />
                      </IconButton>
                    </Inline>
                    <LiveJoinNowButton
                      onClick={() => {
                        navigate(
                          generatePath('/:customerSite/live/:webChannelId', {
                            customerSite,
                            webChannelId,
                          })
                        );
                        gtag('event', GOOGLE_EVENTS.LIVE_STREAM_CLICK, { live_cta: 'fullscreen toast' });
                      }}
                      showJoinEarly={showJoinEarly}
                    />
                  </Stack>
                ),
                customCss: {
                  container: () => ({
                    backgroundColor: theme.palette.surface.canvas,
                    borderRadius: theme.shape.borderRadius.m,
                    pointerEvents: 'auto',
                  }),
                },
              },
              fullScreenToastId,
              'top-right'
            );
        } else {
          hasTriggeredToast.current = false;
          remove(fullScreenToastId);
        }
      }
    };
    handleFullscreenChange();

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      remove(fullScreenToastId);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [
    channelId,
    customerSite,
    fullScreenToastId,
    isLive,
    navigate,
    prefixNS,
    remove,
    showJoinEarly,
    theme.palette.contrast,
    theme.palette.surface.canvas,
    theme.shape.borderRadius.m,
    theme.typography.pxToRem,
    trigger,
    videoName,
    webChannelId,
  ]);

  const appendScript = (src: string, scriptId = '') => {
    const script = document.createElement('script');
    if (scriptId) script.id = scriptId;

    script.src = src;
    document.body.append(script);
  };

  const scriptCleanup = () => {
    // Remove styling link each time as it gets appended multiple times
    [...document.getElementsByTagName('link')].find((x) => x.href.includes('control.resi.io'))?.remove();
    const scripts = document.body.getElementsByTagName('script');
    let i = scripts.length - 1;
    while (i > -1) {
      scripts[i].remove();
      i--;
    }
  };

  React.useEffect(() => {
    const LOADER_ID = 'resi-loader-script';
    const PAGE_LOADER_ID = 'resi-page-loader-script';

    if (playerKey) {
      scriptCleanup();
      appendScript(webplayerPageScript, PAGE_LOADER_ID);
      appendScript(webplayerLoader, LOADER_ID);
    }

    return () => {
      scriptCleanup();
    };
  }, [playerKey, webplayerLoader, webplayerPageScript]);

  React.useEffect(() => {
    const isLibraryReady = type === EMBED_TYPE.LIBRARY && currentVideo;
    const isWebChannelReady = type === EMBED_TYPE.WEB_CHANNEL && channelId;
    if (isLibraryReady || isWebChannelReady) {
      setPlayerState(playerCommands.LOAD);
    }
  }, [type, channelId, currentVideo, playerCommands.LOAD, setPlayerState]);

  return (
    <React.Fragment>
      <Global styles={S.global} />
      <div
        key={playerKey}
        ref={handleRef}
        css={css([
          {
            position: 'relative',
          },
          playerState === playerStates.LOADING && S.videoContainerLoading(),
        ])}
        data-autoplay={isAutoPlay}
        data-embed-id={type === EMBED_TYPE.LIBRARY ? videoProp?.embedId : channelId}
        data-env={webplayerEnvAttribute}
        data-mode={mode}
        data-mute={isMuted}
        data-scrub-bar={isScrubBar}
        data-start-pos={getContinueWatchingVideo(videoId)?.currentTime}
        data-testid="resi-video-player"
        data-type={type}
        id="resi-video-player"
        style={{
          position: type === EMBED_TYPE.LIBRARY ? 'absolute' : 'relative',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
        }}
      />
      {playerState === playerStates.LOADING ||
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      !videoContainer ? (
        <div css={S.loadingSpinner} data-testid="loading-container">
          <Progress colorVariant="inactive" />
        </div>
      ) : (
        createPortal(<div data-testid={`${fullScreenToastId}-test-id`} id={fullScreenToastId} />, videoContainer)
      )}
    </React.Fragment>
  );
};

PlayerScriptInternal.displayName = 'PlayerScriptInternal';

/* eslint-disable import/export */
export const PlayerScript = Object.assign(React.forwardRef(PlayerScriptInternal), {
  displayName: 'PlayerScript',
  EMBED_TYPE,
});

// eslint-disable-next-line no-redeclare
export namespace PlayerScript {
  export type EmbedType = _EmbedType;
}
